import type {
  DeliveryContents,
  DeliveryResponse,
} from "@/forwards2/forwardsTypes";
import { sumBy } from "lodash-es";

/**
 * A delivery's contents, or several deliveries' put together, without the one day COD age and RE100 are
 * judged at.
 * Put together, `by_period` names a period once per delivery that holds it.
 */
export type DeliveryProfile = Omit<DeliveryContents, "qualification_date">;

/**
 * Every delivery of the deal in one profile, voided ones left out; null when none has contents (a
 * pay-as-produced deal's months never do).
 *
 * The deliveries' mandate shares are kept apart rather than merged per mandate: each was judged at its
 * own delivery's window end, so one mandate's volume can sit in two COD buckets across deliveries.
 */
export function combinedProfile(
  deliveries: readonly DeliveryResponse[]
): DeliveryProfile | null {
  const contents = deliveries
    .filter((delivery) => delivery.transaction_status !== null)
    .flatMap((delivery) => (delivery.contents ? [delivery.contents] : []));
  if (contents.length === 0) return null;

  return {
    planned_mwh: sumBy(contents, "planned_mwh"),
    by_period: contents.flatMap(({ by_period }) => by_period),
    mandates: contents.flatMap(({ mandates }) => mandates),
    unbacked_mwh: sumBy(contents, "unbacked_mwh"),
  };
}
